import {
  DEFAULT_BASEMENT_LEVEL_TYPE,
  DEFAULT_PARKING_LEVEL_TYPE,
  getLevelTypeName
} from '@/shared/catalogs/level-type'
import { type PaymentMethod, PaymentMethodLabel } from '../catalogs/payment-method'
import { defineVariable, type NegotiationVariable } from '../core/define'

const num = (v?: number): string =>
  v === undefined || v === null || Number.isNaN(v) ? '' : String(v)
const bool = (v?: boolean): string => (v ? 'SI' : 'NO')

/**
 * Resolución de las variables de la NEGOCIACIÓN (acuerdo de pago). Vienen del paymentAgreement.
 *
 * El metadato está en `@idc-forms/schemas/variables/document/negotiation.variables`. Las versiones
 * "en letras" de los campos numéricos son la máscara `letters` (no se duplican acá); para
 * compatibilidad, el orquestador emite los pocos alias `_letras` que existen en plantillas
 * guardadas.
 */
export const NEGOTIATION_VARIABLES: NegotiationVariable[] = [
  defineVariable('upgrades', (c) => bool((c.productionOrder?.length ?? 0) > 0)),
  defineVariable(
    'forma_pago_inmueble',
    (c) => PaymentMethodLabel[c.paymentAgreement?.propertyPaymentMethod as PaymentMethod] ?? ''
  ),
  defineVariable('terreno_extra', (c) => num(c.paymentAgreement?.extraLand)),
  defineVariable('parqueo_sotano', (c) =>
    c.paymentAgreement?.hasParkingAndBasement
      ? (c.paymentAgreement?.detailsParkingAndBasement ?? '')
      : ''
  ),
  defineVariable('valor_total', (c) => c.computed?.valor_total ?? ''),
  defineVariable('reserva', (c) => c.computed?.reserva ?? ''),
  defineVariable('total_cuotas', (c) => c.computed?.total_cuotas ?? ''),
  defineVariable('monto_financiar', (c) => c.computed?.monto_financiar ?? ''),
  defineVariable('valor_total_con_moneda', (c) => c.computed?.valor_total_con_moneda ?? ''),
  defineVariable('fecha_entrega', (c) => c.computed?.fecha_entrega ?? ''),
  defineVariable('fecha_primera_cuota', (c) => c.computed?.fecha_primera_cuota ?? ''),
  defineVariable('fecha_ultima_cuota', (c) => c.computed?.fecha_ultima_cuota ?? ''),
  defineVariable('fecha_inicio_plazo', (c) => c.computed?.fecha_inicio_plazo ?? ''),

  // Parqueo (acuerdo de pago)
  defineVariable('tiene_parqueo', (c) => bool(c.paymentAgreement?.hasParking)),
  defineVariable('parqueo_numero', (c) =>
    c.paymentAgreement?.hasParking ? num(c.paymentAgreement?.parking?.number) : ''
  ),
  defineVariable('parqueo_m2_negociacion', (c) =>
    c.paymentAgreement?.hasParking ? num(c.paymentAgreement?.parking?.areaM2) : ''
  ),
  defineVariable('parqueo_vehiculos', (c) =>
    c.paymentAgreement?.hasParking ? num(c.paymentAgreement?.parking?.vehicles) : ''
  ),
  defineVariable('parqueo_nivel', (c) =>
    c.paymentAgreement?.hasParking ? num(c.paymentAgreement?.parking?.level) : ''
  ),
  defineVariable('parqueo_nivel_tipo', (c) =>
    c.paymentAgreement?.hasParking
      ? getLevelTypeName(c.paymentAgreement?.parking?.levelType, DEFAULT_PARKING_LEVEL_TYPE)
      : ''
  ),

  // Bodega (acuerdo de pago) — la sección se renombró de "Sótano" a "Bodega"
  // Las keys tiene_sotano / sotano_* se conservan para compatibilidad con plantillas existentes.
  // Las keys tiene_bodega / bodega_* son los nombres canónicos para plantillas nuevas.
  defineVariable('tiene_bodega', (c) => bool(c.paymentAgreement?.hasBasement)),
  defineVariable('bodega_numero', (c) =>
    c.paymentAgreement?.hasBasement ? num(c.paymentAgreement?.basement?.storageNumber) : ''
  ),
  defineVariable('bodega_m2', (c) =>
    c.paymentAgreement?.hasBasement ? num(c.paymentAgreement?.basement?.areaM2) : ''
  ),
  defineVariable('bodega_sotano', (c) =>
    c.paymentAgreement?.hasBasement ? num(c.paymentAgreement?.basement?.level) : ''
  ),
  defineVariable('sotano_nivel_tipo', (c) =>
    c.paymentAgreement?.hasBasement
      ? getLevelTypeName(c.paymentAgreement?.basement?.levelType, DEFAULT_BASEMENT_LEVEL_TYPE)
      : ''
  ),

  // Valores sin impuestos (acuerdo de pago) — visibles solo con permiso FieldAmountsWithoutTaxNegotiation
  defineVariable('valor_titulo_sin_impuesto', (c) =>
    c.paymentAgreement?.titleAmountWithoutTax != null
      ? String(c.paymentAgreement.titleAmountWithoutTax)
      : ''
  ),
  defineVariable('valor_total_sin_impuesto', (c) =>
    c.paymentAgreement?.totalAmountWithoutTax != null
      ? String(c.paymentAgreement.totalAmountWithoutTax)
      : ''
  ),
  defineVariable('valor_upgrades_sin_impuesto', (c) =>
    c.paymentAgreement?.upgradesAmountWithoutTax != null
      ? String(c.paymentAgreement.upgradesAmountWithoutTax)
      : ''
  ),
  defineVariable('valor_inmueble_sin_impuesto', (c) =>
    c.paymentAgreement?.propertyAmountWithoutTax != null
      ? String(c.paymentAgreement.propertyAmountWithoutTax)
      : ''
  ),

  // Datos base de la negociación (ya resueltos a string en el orquestador).
  defineVariable('codigo_negociacion', (c) => c.computed?.codigo_negociacion ?? ''),
  defineVariable('estado_negociacion', (c) => c.computed?.estado_negociacion ?? ''),
  defineVariable('fecha_inicio_negociacion', (c) => c.computed?.fecha_inicio_negociacion ?? ''),
  defineVariable('fecha_fin_negociacion', (c) => c.computed?.fecha_fin_negociacion ?? ''),

  // Legacy — compatibilidad con plantillas anteriores
  defineVariable('tiene_sotano', (c) => bool(c.paymentAgreement?.hasBasement)),
  defineVariable('sotano_numero', (c) =>
    c.paymentAgreement?.hasBasement ? num(c.paymentAgreement?.basement?.number) : ''
  ),
  defineVariable('sotano_m2', (c) =>
    c.paymentAgreement?.hasBasement ? num(c.paymentAgreement?.basement?.areaM2) : ''
  ),
  defineVariable('sotano_nivel', (c) =>
    c.paymentAgreement?.hasBasement ? num(c.paymentAgreement?.basement?.level) : ''
  )
]
